/**
 * Opens a downloaded file when its "downloaded" local notification is tapped
 * (the employee payslip download — `screens/my_pay/my_pay_download.ts`).
 *
 * Native-only: in a browser this returns before touching the platform
 * adapter at all. Registered ONCE at module scope in `main.tsx`, before the
 * app renders, so a tap that cold-starts the app is still handled — the
 * Android plugin retains that tap until a listener subscribes. Opening an
 * already-saved local file needs no session, so it does not wait for auth.
 */
import { platform } from '@ledger/platform';
import type { NotifyPayload } from '@ledger/platform';
import { isNative } from './platform.js';
import { dismiss_device_notifications_for_uri } from '../data/device_notifications.js';

export interface NativeFileOpenDeps {
  is_native(): boolean;
  on_tap(listener: (payload: NotifyPayload) => void): () => void;
  open(uri: string, mime_type: string): Promise<boolean>;
  /** Clear the matching in-app bell entry — the employee has acted on it. */
  dismiss_bell_entry(uri: string): void;
}

function default_deps(): NativeFileOpenDeps {
  return {
    is_native: isNative,
    on_tap: (listener) => platform.onNotificationTap(listener),
    open: (uri, mime_type) => platform.openFile(uri, mime_type),
    dismiss_bell_entry: dismiss_device_notifications_for_uri,
  };
}

/** Returns an unsubscribe function (a no-op off-device). */
export function register_native_file_open_on_notification_tap(
  deps: NativeFileOpenDeps = default_deps(),
): () => void {
  if (!deps.is_native()) return () => undefined;
  return deps.on_tap((payload) => {
    const { href, mimeType } = payload;
    // Only notifications that point at a saved file carry both.
    if (href === undefined || mimeType === undefined) return;
    deps.dismiss_bell_entry(href);
    deps.open(href, mimeType).catch((err: unknown) => {
      console.error('[wagepro-web] opening the downloaded file failed', err);
    });
  });
}

/**
 * Open a downloaded file from inside the app — the bell's "payslip
 * downloaded" entry. Never throws: a file that cannot be opened (deleted,
 * no viewer) is logged, and native `openFile` offers the share sheet itself.
 */
export function open_downloaded_file(
  uri: string,
  mime_type: string,
  deps: Pick<NativeFileOpenDeps, 'open'> = default_deps(),
): void {
  deps.open(uri, mime_type).catch((err: unknown) => {
    console.error('[wagepro-web] opening the downloaded file failed', err);
  });
}
